import React from 'react';
import { ROW_HEIGHT } from './constants';

type WorkSchedule = {
  id: string | number;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
};

type Props = {
  weekdayIdx: number;
  rowStartMin: number;
  rowEndMin: number;
  template: WorkSchedule[];
  editMode?: boolean;
  deletedScheduleIds?: Set<string | number>;
};

function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

const ScheduleOverlay: React.FC<Props> = ({ weekdayIdx, rowStartMin, rowEndMin, template, editMode = false, deletedScheduleIds = new Set() }) => {
  const remToPx = (rem: number): number => {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return rem * fontSize;
  };
  
  const ROW_HEIGHT_PX = remToPx(ROW_HEIGHT);
  
  const schedulesToday = template.filter(t => t.active && t.weekday === weekdayIdx);
  const segments = schedulesToday.map(t => {
    const tStart = timeToMinutes(t.startTime);
    const tEnd = timeToMinutes(t.endTime);
    const wraps = tEnd <= tStart;
    if (!wraps) {
      const segStart = Math.max(rowStartMin, tStart);
      const segEnd = Math.min(rowEndMin, tEnd);
      const dur = Math.max(0, segEnd - segStart);
      if (dur <= 0) return null;
      const offsetPx = Math.round(((segStart - rowStartMin) / 60) * ROW_HEIGHT_PX);
      const heightPx = Math.max(2, Math.round((dur / 60) * ROW_HEIGHT_PX));
      return { offsetPx, heightPx, id: t.id, scheduleId: t.id };
    }
    if (wraps) {
      const seg1Start = Math.max(rowStartMin, 0);
      const seg1End = Math.min(rowEndMin, tEnd);
      const dur1 = Math.max(0, seg1End - seg1Start);
      const parts: { offsetPx: number; heightPx: number; id: string | number; scheduleId: string | number }[] = [];
      if (dur1 > 0) {
        const offsetPx1 = Math.round(((seg1Start - rowStartMin) / 60) * ROW_HEIGHT_PX);
        const heightPx1 = Math.max(2, Math.round((dur1 / 60) * ROW_HEIGHT_PX));
        parts.push({ offsetPx: offsetPx1, heightPx: heightPx1, id: `${t.id}-early`, scheduleId: t.id });
      }
      const seg2Start = Math.max(rowStartMin, tStart);
      const seg2End = Math.min(rowEndMin, 24 * 60);
      const dur2 = Math.max(0, seg2End - seg2Start);
      if (dur2 > 0) {
        const offsetPx2 = Math.round(((seg2Start - rowStartMin) / 60) * ROW_HEIGHT_PX);
        const heightPx2 = Math.max(2, Math.round((dur2 / 60) * ROW_HEIGHT_PX));
        parts.push({ offsetPx: offsetPx2, heightPx: heightPx2, id: `${t.id}-late`, scheduleId: t.id });
      }
      return parts.length ? parts : null;
    }
    return null;
  }).flat().filter(Boolean) as { offsetPx: number; heightPx: number; id: string | number; scheduleId: string | number }[];

  if (segments.length === 0) return null;
  return (
    <>
      {segments.map(seg => {
        const isDeleted = deletedScheduleIds.has(seg.scheduleId);
        const bgColor = !editMode
          ? 'transparent'
          : isDeleted
            ? 'repeating-linear-gradient(135deg, rgba(180,180,180,0.25), rgba(180,180,180,0.25) 4px, rgba(240,240,240,0.35) 4px, rgba(240,240,240,0.35) 8px)'
            : 'rgba(139, 92, 246, 0.15)';
        return (
          <div key={`seg-${weekdayIdx}-${seg.id}`} style={{ position: 'absolute', top: seg.offsetPx, left: 0, right: 0, height: seg.heightPx, zIndex: 1, background: bgColor, pointerEvents: 'none' }} />
        );
      })}
    </>
  );
};

export default ScheduleOverlay;


